// Renders score sheets into self-contained, print-optimized HTML documents:
// one page per group for the round-robin stage, and one page per match for the
// playoff bracket. The output is a plain white page with blank cells the
// players fill in by hand — deliberately independent of the app's dark MUI
// theme so it prints cleanly with black ink on white paper.
//
// This is a pure string builder (no DOM, no React) so it can be unit-tested.
// The thin window/print wiring lives in `printScoreSheet.ts`.
//
// Sheets are localised through the shared i18n core: callers pass the active
// `Locale` (defaulting to English) and every fixed label resolves through the
// `admin.scoresheet.*` catalogue keys.

import type {
  BracketScoreSheet,
  GroupScoreSheet,
  ScoreSheetMatch,
} from '../domain';
import {
  translate,
  DEFAULT_LOCALE,
  LOCALE_META,
  type Locale,
  type TranslateParams,
} from '../i18n/messages';

/** A `translate` bound to one locale, rooted at the score-sheet namespace. */
type SheetTranslator = (key: string, params?: TranslateParams) => string;

function sheetTranslator(locale: Locale): SheetTranslator {
  return (key, params) => translate(locale, `admin.scoresheet.${key}`, params);
}

/** Escapes text for safe interpolation into HTML. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** A roster row: seed, id and name, plus blanks for the final tally. */
function rosterRows(sheet: GroupScoreSheet): string {
  return sheet.players
    .map(
      (p) => `
          <tr>
            <td class="seed">${escapeHtml(p.id)}</td>
            <td class="name">${escapeHtml(p.name)}</td>
            <td class="tally"></td>
            <td class="tally"></td>
            <td class="tally"></td>
            <td class="tally"></td>
          </tr>`,
    )
    .join('');
}

/** Blank per-game grid for one match: a header row plus a row per player. */
function gamesTable(
  nameA: string,
  nameB: string,
  bestOf: number,
  t: SheetTranslator,
): string {
  const gameHeaders = Array.from(
    { length: bestOf },
    (_, i) => `<th class="game">${escapeHtml(t('gameHeader', { n: i + 1 }))}</th>`,
  ).join('');
  const gameCells = Array.from(
    { length: bestOf },
    () => '<td class="game"></td>',
  ).join('');

  return `<table class="games">
            <thead>
              <tr>
                <th class="who">${escapeHtml(t('playerHeader'))}</th>
                ${gameHeaders}
                <th class="won">${escapeHtml(t('gamesHeader'))}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th class="who">${escapeHtml(nameA)}</th>
                ${gameCells}
                <td class="won"></td>
              </tr>
              <tr>
                <th class="who">${escapeHtml(nameB)}</th>
                ${gameCells}
                <td class="won"></td>
              </tr>
            </tbody>
          </table>`;
}

/** One match block: meta line + a per-game grid + a winner line. */
function matchBlock(match: ScoreSheetMatch, t: SheetTranslator): string {
  return `
        <div class="match">
          <div class="match-head">
            <span class="match-no">${escapeHtml(t('matchNo', { order: match.order }))}</span>
            <span class="match-meta">${escapeHtml(
              t('matchMeta', {
                timeSlot: match.timeSlot,
                tableIndex: match.tableIndex,
                bestOf: match.bestOf,
                gamesToWin: match.gamesToWin,
              }),
            )}</span>
          </div>
          ${gamesTable(match.playerA.name, match.playerB.name, match.bestOf, t)}
          <div class="winner">${escapeHtml(t('winner'))} <span class="blank"></span></div>
        </div>`;
}

/** The full single-group sheet (one printed page). */
function groupSheet(sheet: GroupScoreSheet, t: SheetTranslator): string {
  const matches = sheet.matches.map((m) => matchBlock(m, t)).join('');
  // Long best-of series make each match table too wide for two columns on A4,
  // so they get a single full-width column instead.
  const maxBestOf = sheet.matches.reduce(
    (max, m) => Math.max(max, m.bestOf),
    sheet.bestOf,
  );
  const matchesClass = maxBestOf >= 7 ? 'matches matches--single' : 'matches';
  return `
    <section class="sheet">
      <header class="sheet-head">
        <div class="tournament">${escapeHtml(sheet.tournamentName)}</div>
        <h1 class="group">${escapeHtml(t('group', { label: sheet.poolLabel }))}</h1>
        <div class="summary">${escapeHtml(
          t('groupSummary', {
            players: sheet.players.length,
            matches: sheet.matches.length,
            bestOf: sheet.bestOf,
          }),
        )}</div>
      </header>

      <h2 class="section-title">${escapeHtml(t('rosterTitle'))}</h2>
      <table class="roster">
        <thead>
          <tr>
            <th class="seed">${escapeHtml(t('seedHeader'))}</th>
            <th class="name">${escapeHtml(t('playerHeader'))}</th>
            <th class="tally">${escapeHtml(t('wonHeader'))}</th>
            <th class="tally">${escapeHtml(t('gamesDiffHeader'))}</th>
            <th class="tally">${escapeHtml(t('pointsDiffHeader'))}</th>
            <th class="tally">${escapeHtml(t('rankHeader'))}</th>
          </tr>
        </thead>
        <tbody>${rosterRows(sheet)}
        </tbody>
      </table>

      <h2 class="section-title">${escapeHtml(t('resultsTitle'))}</h2>
      <div class="${matchesClass}">${matches}
      </div>
    </section>`;
}

/** The full single-bracket-match sheet (one printed page). */
function bracketSheet(sheet: BracketScoreSheet, t: SheetTranslator): string {
  return `
    <section class="sheet">
      <header class="sheet-head">
        <div class="tournament">${escapeHtml(sheet.tournamentName)}</div>
        <h1 class="group">${escapeHtml(sheet.nodeLabel)}</h1>
        <div class="summary">${escapeHtml(
          t('vs', { a: sheet.playerA.name, b: sheet.playerB.name }),
        )} · ${escapeHtml(t('bestOfMeta', { bestOf: sheet.bestOf, gamesToWin: sheet.gamesToWin }))}</div>
      </header>

      <h2 class="section-title">${escapeHtml(t('resultTitle'))}</h2>
      <div class="matches matches--single">
        <div class="match">
          <div class="match-head">
            <span class="match-no">${escapeHtml(sheet.nodeLabel)}</span>
            <span class="match-meta">${escapeHtml(t('bestOfMeta', { bestOf: sheet.bestOf, gamesToWin: sheet.gamesToWin }))}</span>
          </div>
          ${gamesTable(sheet.playerA.name, sheet.playerB.name, sheet.bestOf, t)}
          <div class="winner">${escapeHtml(t('winner'))} <span class="blank"></span></div>
        </div>
      </div>
    </section>`;
}

const STYLES = `
    :root { color-scheme: light; }
    * { box-sizing: border-box; }
    html, body { background: #fff; color: #000; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      font-size: 12px;
      line-height: 1.35;
      padding: 16px;
      /* Keep header / column shading when printed (off by default in browsers). */
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .sheet { max-width: 760px; margin: 0 auto 24px; }
    .sheet-head { border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 12px; }
    .tournament { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; color: #444; }
    .group { font-size: 26px; margin: 2px 0 0; }
    .summary { font-size: 12px; color: #444; margin-top: 2px; }
    .section-title { font-size: 13px; text-transform: uppercase; letter-spacing: 0.05em; margin: 16px 0 6px; }
    table { border-collapse: collapse; width: 100%; }
    .roster th, .roster td { border: 1px solid #999; padding: 4px 6px; text-align: left; }
    .roster thead th { background: #eee; font-size: 11px; }
    .roster .seed { width: 48px; color: #444; }
    .roster .tally { width: 70px; height: 28px; }
    .matches { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
    .matches.matches--single { grid-template-columns: 1fr; }
    .match { border: 1px solid #999; border-radius: 4px; padding: 8px; break-inside: avoid; page-break-inside: avoid; }
    .match-head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; margin-bottom: 6px; }
    .match-no { font-weight: 700; }
    .match-meta { font-size: 10px; color: #555; text-align: right; }
    .games th, .games td { border: 1px solid #999; padding: 3px 4px; text-align: center; }
    .games thead th { background: #eee; font-size: 10px; }
    .games .who { text-align: left; min-width: 90px; max-width: 150px; overflow-wrap: anywhere; font-weight: 600; }
    .games .game { width: 32px; height: 30px; }
    .games .won { width: 38px; background: #f5f5f5; }
    .winner { margin-top: 6px; font-size: 11px; }
    .winner .blank { display: inline-block; min-width: 160px; border-bottom: 1px solid #000; height: 14px; }
    @media print {
      body { padding: 0; }
      .sheet { max-width: none; margin: 0; }
      .sheet:not(:last-child) { page-break-after: always; }
    }
    @page { size: A4; margin: 14mm; }
`;

/** Wraps rendered sheet sections into a complete standalone HTML document. */
function sheetDocument(title: string, body: string, locale: Locale): string {
  return `<!DOCTYPE html>
<html lang="${LOCALE_META[locale].code}">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(title)}</title>
    <style>${STYLES}</style>
  </head>
  <body>
${body}
  </body>
</html>`;
}

/**
 * Builds a complete printable HTML document for the given group score sheets,
 * one page per group, in `locale` (English when omitted). Safe to write into a
 * new window or iframe and print.
 */
export function renderScoreSheetHtml(
  sheets: readonly GroupScoreSheet[],
  locale: Locale = DEFAULT_LOCALE,
): string {
  const t = sheetTranslator(locale);
  const title = sheets[0]
    ? t('groupDocTitle', { name: sheets[0].tournamentName })
    : t('groupDocTitleEmpty');
  const body = sheets.length
    ? sheets.map((sheet) => groupSheet(sheet, t)).join('\n')
    : `<p>${escapeHtml(t('noGroups'))}</p>`;
  return sheetDocument(title, body, locale);
}

/**
 * Builds a complete printable HTML document for the given bracket match score
 * sheets, one page per match. Safe to write into a new window or iframe and
 * print. The usual case is a single match (the Bracket view prints one card at
 * a time), so a lone sheet is titled after its match — the title names the
 * print job and any saved PDF.
 */
export function renderBracketSheetHtml(
  sheets: readonly BracketScoreSheet[],
  locale: Locale = DEFAULT_LOCALE,
): string {
  const t = sheetTranslator(locale);
  const title =
    sheets.length === 1
      ? t('bracketDocTitleSingle', {
          name: sheets[0].tournamentName,
          label: sheets[0].nodeLabel,
        })
      : sheets[0]
        ? t('bracketDocTitle', { name: sheets[0].tournamentName })
        : t('bracketDocTitleEmpty');
  const body = sheets.length
    ? sheets.map((sheet) => bracketSheet(sheet, t)).join('\n')
    : `<p>${escapeHtml(t('noMatches'))}</p>`;
  return sheetDocument(title, body, locale);
}
